// Considered: ApproachDetailPageOrganism — rejected because it hosts the
// approach deploy wizard (ApproachDeployWizardOrganism), inseparable from
// approach semantics; this shows a type-specific preview + Reference/Fork
// CTAs instead. See spec Design System Reuse Audit (D4).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { useNavigate } from 'react-router-dom';
import { TabView, TabPanel, TabViewTabChangeEvent } from 'primereact/tabview';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useItemDetail } from '../../../hooks/useItemDetail';
import { useItemAudit } from '../../../hooks/useItemAudit';
import { useChannelDetail } from '../../../hooks/useChannelDetail';
import { useChannelItemManageAccess } from '../../../hooks/useChannelItemManageAccess';
import {
  referenceChannelItem,
  forkContent,
  updateForkPolicy,
  addItemManager,
  removeItemManager,
  addForkGrant,
  removeForkGrant,
} from '../../../api/marketplaceApi';
import type { AspectGroupShelf, ForkPolicy } from 'types/channel';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ItemTypeGlyphAtom from '../../atoms/marketplace/ItemTypeGlyphAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import {
  MARKETPLACE_ITEM_TYPE_ICONS,
  MARKETPLACE_ITEM_TYPE_TONES,
} from '../../atoms/marketplace/iconMaps';
import { getMarketplaceItemType } from '../../../utils/marketplace/lookups';
import ItemDetailTabMolecule from '../../molecules/marketplace/ItemDetailTabMolecule/ItemDetailTabMolecule';
import ItemPermissionsTabMolecule from '../../molecules/marketplace/ItemPermissionsTabMolecule/ItemPermissionsTabMolecule';
import ItemAuditTabMolecule from '../../molecules/marketplace/ItemAuditTabMolecule/ItemAuditTabMolecule';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import './TemplateDetailPageOrganism.scss';

export interface TemplateDetailPageOrganismProps {
  channelId: string;
  itemId: string;
}

const ITEM_TYPE_KEYS: Record<string, string> = {
  schema: 'marketplace.common.itemType.schema',
  context: 'marketplace.common.itemType.context',
  arc_template: 'marketplace.common.itemType.arcTemplate',
  orbit_template: 'marketplace.common.itemType.orbitTemplate',
  metaspace: 'marketplace.common.itemType.metaspace',
  aspect: 'marketplace.common.itemType.aspect',
  aspect_group: 'marketplace.common.itemType.aspectGroup',
};

/**
 * Map a failed reference/fork to the message that tells the user what to do next.
 *
 * Both handlers previously used a bare `catch {}` and rendered one generic "Failed to ..."
 * string, which collapsed three outcomes the user can act on very differently: 403 (not
 * permitted / author disabled forking), 409 (already referenced or forked — nothing is wrong),
 * and 404 (the item is gone). Reporting "already forked" as a failure is actively misleading.
 * Anything else keeps the generic message.
 */
function ctaErrorKey(kind: 'reference' | 'fork', err: unknown): string {
  const base = `marketplace.templateDetail.actions.${kind}`;
  switch ((err as { response?: { status?: number } })?.response?.status) {
    case 403:
      return `${base}Forbidden`;
    case 409:
      return `${base}Conflict`;
    case 404:
      return `${base}NotFound`;
    default:
      return `${base}Error`;
  }
}

/**
 * Renders an `aspect_group` item's live membership as a names-only nested list — unresolvable
 * and unpublished members never reach this list (the server already filtered them out, see
 * aspectGroupShelfHelpers.ts), so what's here is exactly what's safe to show. Recursion is
 * bounded server-side (`NESTING_MAX_DEPTH`); this function has no depth guard of its own.
 */
function renderGroupMembershipList(
  shelf: AspectGroupShelf,
  t: (key: string, options?: Record<string, unknown>) => string
): React.ReactNode {
  if (shelf.aspects.length === 0 && shelf.subgroups.length === 0) {
    return (
      <p className="marketplace-template-detail__group-membership-empty">
        {t('marketplace.templateDetail.groupMembership.empty')}
      </p>
    );
  }

  return (
    <ul className="marketplace-template-detail__group-membership-list">
      {shelf.aspects.map((systemName) => (
        <li key={systemName} className="marketplace-template-detail__group-membership-item">
          {systemName}
        </li>
      ))}
      {shelf.subgroups.map((subgroup) => (
        <li key={subgroup.systemName} className="marketplace-template-detail__group-membership-item">
          {getLocalizedText(subgroup.name) || subgroup.systemName}
          {renderGroupMembershipList(subgroup, t)}
        </li>
      ))}
    </ul>
  );
}

/**
 * Generic per-type detail host for the template-supertype channel item types
 * (schema/arc_template/context/orbit_template/metaspace/aspect/aspect_group).
 * Unlike ApproachDetailPageOrganism, this shows a type-specific preview +
 * Reference (subscribe, no copy) and Fork (the only copy path) CTAs rather
 * than a deploy wizard — see the spec's Design System Reuse Audit for why
 * approach keeps its own organism.
 */
const TemplateDetailPageOrganism: React.FC<TemplateDetailPageOrganismProps> = ({
  channelId,
  itemId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const { channel } = useChannelDetail(channelId);
  const detail = useItemDetail(channelId, itemId);
  const audit = useItemAudit(channelId, itemId);

  const { canManage } = useChannelItemManageAccess({
    itemType: detail.item?.item_type ?? 'schema',
    itemSystemName: detail.item?.item_system_name,
  });

  const [activeIndex, setActiveIndex] = useState(0);
  const [isMutating, setIsMutating] = useState(false);
  const [isReferencing, setIsReferencing] = useState(false);
  const [isForking, setIsForking] = useState(false);
  const [ctaResult, setCtaResult] = useState<{
    kind: 'reference' | 'fork';
    success: boolean;
    message: string;
  } | null>(null);

  // Composed from the same keys as ChannelDetailOrganism rather than a hardcoded
  // "<name> - Marketplace - Center". Restoring the captured title on unmount (instead of
  // resetting to a literal) keeps concurrently-rendered organisms from clobbering each
  // other's titles — see the matching comment there.
  useEffect(() => {
    if (!detail.item) return;
    const name =
      getLocalizedText(detail.item.item_metadata?.name) || detail.item.item_system_name;
    const previous = document.title;
    document.title = `${name} - ${t('marketplace.pageTitle')} - ${t('header.title')}`;
    return () => {
      document.title = previous;
    };
  }, [detail.item, t]);

  const showManagementTabs = canManage;

  const tabs = useMemo<Array<'details' | 'permissions' | 'audit'>>(() => {
    const list: Array<'details' | 'permissions' | 'audit'> = ['details'];
    if (showManagementTabs) {
      list.push('permissions', 'audit');
    }
    return list;
  }, [showManagementTabs]);

  const handleTabChange = useCallback((e: TabViewTabChangeEvent) => {
    setActiveIndex(e.index);
  }, []);

  const runMutation = useCallback(
    async (fn: () => Promise<void>) => {
      setIsMutating(true);
      try {
        await fn();
        detail.refetch();
        audit.refetch();
      } finally {
        setIsMutating(false);
      }
    },
    [detail, audit],
  );

  const handleUpdateForkPolicy = useCallback(
    async (nextPolicy: ForkPolicy) => {
      if (!apiClients || !organizationId) return;
      await runMutation(() =>
        updateForkPolicy(apiClients.schemaClient, channelId, itemId, nextPolicy),
      );
    },
    [apiClients, organizationId, channelId, itemId, runMutation],
  );

  const handleAddManager = useCallback(
    async (identityId: string) => {
      if (!apiClients || !organizationId) return;
      await runMutation(() =>
        addItemManager(apiClients.schemaClient, channelId, itemId, identityId),
      );
    },
    [apiClients, organizationId, channelId, itemId, runMutation],
  );

  const handleRemoveManager = useCallback(
    async (identityId: string) => {
      if (!apiClients || !organizationId) return;
      await runMutation(() =>
        removeItemManager(apiClients.schemaClient, channelId, itemId, identityId),
      );
    },
    [apiClients, organizationId, channelId, itemId, runMutation],
  );

  const handleAddForkGrant = useCallback(
    async (identityId: string) => {
      if (!apiClients || !organizationId) return;
      await runMutation(() =>
        addForkGrant(apiClients.schemaClient, channelId, itemId, identityId),
      );
    },
    [apiClients, organizationId, channelId, itemId, runMutation],
  );

  const handleRemoveForkGrant = useCallback(
    async (identityId: string) => {
      if (!apiClients || !organizationId) return;
      await runMutation(() =>
        removeForkGrant(apiClients.schemaClient, channelId, itemId, identityId),
      );
    },
    [apiClients, organizationId, channelId, itemId, runMutation],
  );

  // Reference/fork target org is derived server-side from the access token, so
  // these handlers deliberately take no organizationId.
  const handleReference = useCallback(async () => {
    if (!apiClients) return;
    setIsReferencing(true);
    setCtaResult(null);
    try {
      await referenceChannelItem(apiClients.schemaClient, channelId, itemId);
      setCtaResult({
        kind: 'reference',
        success: true,
        message: t('marketplace.templateDetail.actions.referenceSuccess'),
      });
    } catch (err) {
      setCtaResult({
        kind: 'reference',
        success: false,
        message: t(ctaErrorKey('reference', err)),
      });
    } finally {
      setIsReferencing(false);
    }
  }, [apiClients, channelId, itemId, t]);

  const handleFork = useCallback(async () => {
    if (!apiClients) return;
    setIsForking(true);
    setCtaResult(null);
    try {
      await forkContent(apiClients.schemaClient, channelId, itemId);
      setCtaResult({
        kind: 'fork',
        success: true,
        message: t('marketplace.templateDetail.actions.forkSuccess'),
      });
    } catch (err) {
      setCtaResult({
        kind: 'fork',
        success: false,
        message: t(ctaErrorKey('fork', err)),
      });
    } finally {
      setIsForking(false);
    }
  }, [apiClients, channelId, itemId, t]);

  if (detail.loading && !detail.item) {
    return (
      <div className="marketplace-template-detail" data-testid="template-detail-loading">
        <span className="marketplace-marketplace__spinner" />
        <p>{t('marketplace.templateDetail.loading')}</p>
      </div>
    );
  }

  if (detail.error && !detail.item) {
    return (
      <div className="marketplace-template-detail" data-testid="template-detail-error">
        <p>{t('marketplace.templateDetail.loadError')}</p>
        <ButtonAtom
          variant="secondary"
          size="small"
          onClick={detail.refetch}
          label={t('marketplace.common.retry')}
        />
      </div>
    );
  }

  if (!detail.item) {
    return (
      <div className="marketplace-template-detail" data-testid="template-detail-not-found">
        <p>{t('marketplace.templateDetail.notFound')}</p>
      </div>
    );
  }

  const item = detail.item;
  const marketplaceType = getMarketplaceItemType(item.item_type);
  const typeLabel = t(ITEM_TYPE_KEYS[item.item_type] ?? 'marketplace.common.itemType.schema');
  const displayName = getLocalizedText(item.item_metadata?.name) || item.item_system_name;
  const displayDescription = getLocalizedText(item.item_metadata?.description);
  const activeTab = tabs[activeIndex] ?? 'details';
  const displayChannelName = getLocalizedText(channel?.name);

  return (
    <div className="marketplace-template-detail" data-testid="template-detail-page">
      <nav className="marketplace-template-detail__breadcrumb" aria-label={t('marketplace.templateDetail.breadcrumbLabel')}>
        <button
          type="button"
          className="marketplace-template-detail__breadcrumb-link"
          onClick={() => navigate('/marketplace')}
        >
          {t('marketplace.browse.title')}
        </button>
        <span className="marketplace-template-detail__breadcrumb-sep" aria-hidden="true">›</span>
        <button
          type="button"
          className="marketplace-template-detail__breadcrumb-link"
          onClick={() => navigate(`/marketplace/channels/${channelId}`)}
        >
          {displayChannelName || t('marketplace.channels.detail.backToMarketplace')}
        </button>
        <span className="marketplace-template-detail__breadcrumb-sep" aria-hidden="true">›</span>
        <span className="marketplace-template-detail__breadcrumb-current">{displayName}</span>
      </nav>

      <div className="marketplace-template-detail__header">
        <ItemTypeGlyphAtom
          type={marketplaceType}
          size={48}
          ariaLabel={typeLabel}
          className="marketplace-template-detail__glyph"
        />
        <div className="marketplace-template-detail__header-info">
          <h1 className="marketplace-template-detail__name">{displayName}</h1>
          <div className="marketplace-template-detail__meta">
            <TagAtom
              tone={MARKETPLACE_ITEM_TYPE_TONES[marketplaceType]}
              icon={MARKETPLACE_ITEM_TYPE_ICONS[marketplaceType]}
              label={typeLabel}
              size="small"
            />
            {item.item_version != null && (
              <span className="marketplace-template-detail__version">
                {t('marketplace.common.version', { version: item.item_version })}
              </span>
            )}
          </div>
          {displayDescription && (
            <p className="marketplace-template-detail__description">{displayDescription}</p>
          )}
        </div>
        <div className="marketplace-template-detail__cta-area">
          <ButtonAtom
            variant="secondary"
            onClick={handleReference}
            disabled={isReferencing}
            label={
              isReferencing
                ? t('marketplace.templateDetail.actions.referencing')
                : t('marketplace.templateDetail.actions.reference')
            }
            data-testid="template-detail-reference-btn"
          />
          <ButtonAtom
            variant="primary"
            onClick={handleFork}
            disabled={isForking}
            label={
              isForking
                ? t('marketplace.templateDetail.actions.forking')
                : t('marketplace.templateDetail.actions.fork')
            }
            data-testid="template-detail-fork-btn"
          />
        </div>
      </div>

      {ctaResult && (
        <output
          className={`marketplace-template-detail__toast marketplace-template-detail__toast--${
            ctaResult.success ? 'success' : 'error'
          }`}
          // `<output>` carries an implicit role of "status"; failures escalate to "alert".
          role={ctaResult.success ? undefined : 'alert'}
          data-testid={`template-detail-${ctaResult.kind}-toast`}
        >
          <span>{ctaResult.message}</span>
          <button
            type="button"
            className="marketplace-template-detail__toast-close"
            onClick={() => setCtaResult(null)}
          >
            &times;
          </button>
        </output>
      )}

      <div className="marketplace-template-detail__tabs">
        <TabView activeIndex={activeIndex} onTabChange={handleTabChange} scrollable>
          {tabs.map((tab) => (
            <TabPanel key={tab} header={t(`marketplace.itemDetail.tabs.${tab}`)} />
          ))}
        </TabView>
      </div>

      <div className="marketplace-template-detail__body" data-testid="template-detail-body">
        {activeTab === 'details' && (
          <ItemDetailTabMolecule
            item={item}
            syndicationInfo={detail.syndicationInfo}
            canEdit={false}
            headingLevel={2}
          />
        )}

        {activeTab === 'details' && item.item_type === 'aspect_group' && item.aspect_group_shelf && (
          <div
            className="marketplace-template-detail__group-membership"
            data-testid="template-detail-group-membership"
          >
            <h2 className="marketplace-template-detail__group-membership-title">
              {t('marketplace.templateDetail.groupMembership.title')}
            </h2>
            {renderGroupMembershipList(item.aspect_group_shelf, t)}
          </div>
        )}

        {activeTab === 'permissions' && showManagementTabs && (
          <ItemPermissionsTabMolecule
            forkPolicy={detail.permissions?.forkPolicy ?? 'anyone'}
            managers={detail.permissions?.managers ?? []}
            forkGrants={detail.permissions?.forkGrants ?? []}
            isMutating={isMutating}
            loading={detail.loading && !detail.permissions}
            error={detail.error ? new Error(detail.error) : null}
            canManage={canManage}
            onUpdateForkPolicy={handleUpdateForkPolicy}
            onAddManager={handleAddManager}
            onRemoveManager={handleRemoveManager}
            onAddForkGrant={handleAddForkGrant}
            onRemoveForkGrant={handleRemoveForkGrant}
          />
        )}

        {activeTab === 'audit' && showManagementTabs && (
          <ItemAuditTabMolecule
            entries={audit.entries}
            hasMore={audit.hasMore}
            loading={audit.loading}
            error={audit.error}
            onLoadMore={audit.loadMore}
          />
        )}
      </div>
    </div>
  );
};

TemplateDetailPageOrganism.displayName = 'TemplateDetailPageOrganism';

export default TemplateDetailPageOrganism;
